import { type FC, type ReactNode, useMemo, useRef, useEffect } from "react";
import { useLocation } from "react-router";
import { AssistantRuntimeProvider, useLocalRuntime } from "@assistant-ui/react";
import { useAuthContext } from "@acto/web-common/auth";
import { useLocale } from "@acto/web-common/locales";
import { createIntelligenceAdapter } from "./IntelligenceAdapter";
import type { CustomerContext } from "./types";
import { useChatEnvelope } from "@acto/taskpane/core/chat-envelope";

interface AssistantProviderProps {
  children: ReactNode;
  customerContext?: CustomerContext | null;
}

export const AssistantProvider: FC<AssistantProviderProps> = ({ children, customerContext }) => {
  const { accessToken } = useAuthContext();
  const { locale } = useLocale();
  const { getForPath } = useChatEnvelope();
  const location = useLocation();
  const runtimeRef = useRef<ReturnType<typeof useLocalRuntime> | undefined>(undefined);
  const customerContextRef = useRef<CustomerContext | null>(null);
  // The path chat was opened from; the envelope is only sent if it was
  // published from this exact path (guards against stale/mismatched envelopes).
  const fromPathRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    customerContextRef.current = customerContext ?? null;
  }, [customerContext]);

  useEffect(() => {
    fromPathRef.current = (location.state as { from?: string } | null)?.from;
  }, [location.state]);

  const adapter = useMemo(
    () =>
      // Lazy getters required: session ID and customer context only available after runtime initializes
      createIntelligenceAdapter(
        accessToken ?? "",
        () => {
          if (!runtimeRef.current) return "";

          const threadsState = runtimeRef.current.threads.getState();
          return threadsState.threads[0] ?? "";
        },
        () => customerContextRef.current,
        locale,
        () => getForPath(fromPathRef.current),
      ),
    [accessToken, locale, getForPath],
  );

  const runtime = useLocalRuntime(adapter);
  runtimeRef.current = runtime;

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
};
